import { useEffect, useRef, useState } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

declare global {
	interface Window {
		turnstile?: {
			render: (
				el: HTMLElement,
				opts: {
					sitekey: string;
					callback: (token: string) => void;
					"error-callback"?: () => void;
					"expired-callback"?: () => void;
				},
			) => string;
			remove: (widgetId: string) => void;
			reset: (widgetId: string) => void;
		};
	}
}

function loadTurnstileScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		if (window.turnstile?.render) {
			resolve();
			return;
		}
		const resolveIfReady = () => {
			if (window.turnstile?.render) {
				resolve();
				return true;
			}
			return false;
		};

		const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
		if (existing) {
			existing.addEventListener("load", () => resolve(), { once: true });
			existing.addEventListener("error", () => reject(new Error("turnstile_load_failed")), {
				once: true,
			});
			// `load` only fires once. If this script executed before handlers were attached,
			// detect readiness on the next microtask / macrotask so we never hang indefinitely.
			if (resolveIfReady()) return;
			queueMicrotask(() => resolveIfReady());
			window.setTimeout(() => resolveIfReady(), 0);
			return;
		}
		const s = document.createElement("script");
		s.src = SCRIPT_SRC;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error("turnstile_load_failed"));
		document.head.appendChild(s);
	});
}

type Props = {
	siteKey: string;
	disabled?: boolean;
	onToken: (token: string | null) => void;
	/** Increment to force a new challenge (e.g. after starting a poll). */
	resetKey?: number;
};

/**
 * Renders Cloudflare Turnstile when `siteKey` is set. Parent gates submit on `onToken` receiving a non-empty string.
 */
export function TurnstileGate({ siteKey, disabled, onToken, resetKey = 0 }: Props) {
	const hostRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<string | null>(null);
	const onTokenRef = useRef(onToken);
	const [loadError, setLoadError] = useState(false);
	onTokenRef.current = onToken;

	useEffect(() => {
		if (!siteKey.trim()) {
			onTokenRef.current(null);
			setLoadError(false);
			return;
		}
		// While `disabled` (e.g. mid-submit), keep the last token — clearing it races with POST /api/rooms.
		if (disabled) {
			return;
		}

		let canceled = false;
		setLoadError(false);

		(async () => {
			try {
				await loadTurnstileScript();
			} catch {
				if (!canceled) {
					setLoadError(true);
					onTokenRef.current(null);
				}
				return;
			}
			if (canceled || !hostRef.current || !window.turnstile) return;

			const el = hostRef.current;
			el.replaceChildren();

			try {
				const id = window.turnstile.render(el, {
					sitekey: siteKey.trim(),
					callback: (t) => {
						setLoadError(false);
						onTokenRef.current(t);
					},
					"error-callback": () => {
						setLoadError(true);
						onTokenRef.current(null);
					},
					"expired-callback": () => onTokenRef.current(null),
				});
				widgetIdRef.current = id;
			} catch {
				if (!canceled) {
					setLoadError(true);
					onTokenRef.current(null);
				}
			}
		})();

		return () => {
			canceled = true;
			if (widgetIdRef.current && window.turnstile?.remove) {
				try {
					window.turnstile.remove(widgetIdRef.current);
				} catch {
					/* ignore */
				}
				widgetIdRef.current = null;
			}
			hostRef.current?.replaceChildren();
		};
	}, [siteKey, disabled, resetKey]);

	return (
		<div className="space-y-2">
			<div className="min-h-[65px] rounded-xl border border-wi-text/10 bg-wi-bg/40 p-2 [&_iframe]:max-w-full">
				<div ref={hostRef} className="flex min-h-[65px] justify-center" aria-label="Verification" />
			</div>
			{loadError ? (
				<p className="rounded-xl border border-wi-gold/30 bg-wi-gold-soft px-3.5 py-2.5 text-wi-text text-sm leading-relaxed" role="alert">
					Verification couldn’t load. Check your connection or ad blockers, then refresh the page.
				</p>
			) : null}
		</div>
	);
}
